import { useCallback } from "react";
import { Link } from "react-router";
import { Card, CardBody, CardText, Container, Row } from "reactstrap";
import { Toolbar } from "../../../components/Toolbar";
import { BoolCell } from "../../../components/grid/cells/BoolCell";
import { TokenCell } from "../../../components/grid/cells/TokenCell";
import { CountCell } from "../../../components/grid/cells/CountCell";
import { BoolFilter } from "../../../components/inputs/BoolFilter";
import { Routes } from "../../Routes";
import { AdministrateBackgroundExportButton } from "../../components/AdministrateBackgroundExportButton";
import { AdministrateEnumFilter } from "../../components/AdministrateEnumFilter";
import { AdministrateTable } from "../../components/AdministrateTable";
import { SystemConfigLayout } from "../../components/SystemConfigLayout";
import { LabOptionsFilter } from "../../components/filters/LabOptionsFilter";
import { PayorOptionsFilter } from "../../components/filters/PayorOptionsFilter";
import { DESTROY, makeColumns, UPDATE, useAdministrateTable, useCreatePermission } from "../../utils";
import { AddPreprocessingCategoryModal } from "./components/AddPreprocessingCategoryModal";
import { ImportPreprocessingCategoryModal } from "./components/ImportPreprocessingCategoryModal";
import { PreprocessingCategoryActionCell } from "./components/PreprocessingCategoryActionCell";
import { Info } from "lucide-react";

const useColumns = makeColumns(() => [
  {
    id: "id",
    accessor: "id",
    Header: "ID",
    Cell: TokenCell,
    width: 70,
  },
  {
    id: "name",
    accessor: "name",
    Header: "Name",
  },
  {
    id: "lab",
    accessor: "lab.dashboardDisplayName",
    Header: "Lab",
    Cell: ({ row, value }) => (
      <Link to={Routes.lab.url(row.original.lab?.id)}>
        <span className="token">{value}</span>
      </Link>
    ),
    Filter: (props) => <LabOptionsFilter {...props} scope="with_servicing_provider" />,
  },
  {
    id: "payors.id",
    accessor: "payorNames",
    Header: "Payors",
    Cell: ({ value }) => (
      <div title={value}>
        <CountCell count={value?.length} label="Payors" />
      </div>
    ),
    Filter: (props) => <PayorOptionsFilter {...props} isMulti />,
    disableSortBy: true,
    maxWidth: 100,
  },
  {
    id: "tests.id",
    accessor: "testNames",
    Header: "Tests",
    Filter: (props) => <AdministrateEnumFilter {...props} isMulti resourceUrl="/admin/tests.json" valueKey="id" />,
    disableSortBy: true,
  },
  {
    id: "active",
    accessor: "active",
    Header: "Active",
    Cell: BoolCell,
    Filter: BoolFilter,
    width: 100,
  },
  {
    id: "automated",
    accessor: "automated",
    Header: "System",
    Cell: BoolCell,
    Filter: BoolFilter,
    width: 100,
  },
  {
    id: "actions",
    accessor: "Actions",
    Header: "Actions",
    Cell: PreprocessingCategoryActionCell,
    disableFilters: true,
    disableSortBy: true,
  },
]);

export const PreprocessingCategoriesIndex = () => {
  const { tableRef, allFilters, setTableFilters } = useAdministrateTable();
  const columns = useColumns();
  const [canCreate] = useCreatePermission("PreprocessingCategory");

  const onAdd = useCallback(
    (message, errorDetails = []) => {
      tableRef.current?.refresh?.();
      tableRef.current?.setMessage?.(message, errorDetails.length ? "warning" : "success", errorDetails);
    },
    [tableRef]
  );

  return (
    <SystemConfigLayout title="Preprocessing Categories">
      <Container fluid>
        <Row className="align-items-center mb-3">
          <Toolbar className="ml-auto">
            {canCreate && (
              <AdministrateBackgroundExportButton
                outline
                className="me-2"
                exportUrl="/admin/preprocessing_categories/export"
                filters={allFilters}
                ref={tableRef}
              />
            )}

            {/* {canCreate && <ImportPreprocessingCategoryModal outline className="me-2" onImport={onAdd} />} */}

            {canCreate && <AddPreprocessingCategoryModal onAdd={onAdd} color="primary" />}
          </Toolbar>
        </Row>
      </Container>

      <Card color="info" className="bg-opacity-25 mb-3">
        <CardBody>
          <CardText>
            <Info size={24} className="me-2" />
            System Categories are generated automatically and cannot be created manually.
          </CardText>
        </CardBody>
      </Card>

      <AdministrateTable
        resourceUrl={"/admin/preprocessing_categories.json"}
        resourceName={"preprocessing_category"}
        permissions={[UPDATE, DESTROY]}
        onFilteredChange={setTableFilters}
        columns={columns}
        ref={tableRef}
      />
    </SystemConfigLayout>
  );
};
